import { useFeatureIsOn } from '@growthbook/growthbook-react';
import React, { useEffect, useState } from 'react';
import { useHandleRelatedReportClick } from '@/reports/sections/RelatedReportsSection/RelatedReports';
import { useReportRedirect } from '@/reports/useReportRedirect/useReportRedirect';
import {
  Box,
  Switch,
  Text,
  IconButton,
  Grid,
  Menu,
  MenuItem,
} from '@/theme/components';
import { usePagination } from '@/theme/components/Pagination/hooks/usePagination';
import { Icons, useTheme, useMediaQuery } from '@/theme/index';
import { ValidReportTypes } from '@/utils/helperTypes';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { ClearAllNotificationsModal } from './ClearAllNotificationsModal';
import { StyledMenuItem, NotificationDropdownPagination } from './components';
import { NormalizedNotification, NotificationCard } from '../index';

/**
 * The props for the NotificationDropdown component.
 *
 * @typedef {Object} NotificationDropdownProps
 * @property {HTMLElement | null} anchorEl - The element to anchor the dropdown to.
 * @property {boolean} open - Whether the dropdown is open.
 * @property {Function} handleClose - A function to call when the dropdown is closed.
 * @property {NormalizedNotification[]} notifications - An array of notifications to display.
 * @property {Function} onReadNotification - A function to call when a notification is marked as read.
 * @property {Function} onReadAllNotifications - A function to call when all notifications are marked as read.
 * @property {Function} onClearNotification - A function to call when all notifications are cleared.
 */
interface NotificationDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  handleClose: () => void;
  notifications: NormalizedNotification[];
  onReadNotification: (id: number) => void;
  onReadAllNotifications: () => void;
  onClearNotification?: (id: number) => void;
}

/**
 * A dropdown that displays a list of notifications and allows the user to mark them as read.
 *
 * @param {NotificationDropdownProps} props - The component props.
 * @returns {JSX.Element} - The rendered component.
 *
 * @example
 * <NotificationDropdown
 *   anchorEl={document.getElementById('notification-bell')}
 *   open={true}
 *   handleClose={() => console.log('Dropdown closed.')}
 *   notifications={[{ id: 1, title: 'Notification', body: 'This is a notification.' }]}
 *   onReadNotification={(id) => console.log(`Notification ${id} marked as read.`)}
 *   onReadAllNotifications={() => console.log('All notifications marked as read.')}
 *   onClearNotifications={(id) => console.log(`Notification ${id} deleted.`)}
 * />
 */

const PER_PAGE = 8;

export function NotificationDropdown({
  anchorEl,
  open,
  handleClose,
  notifications,
  onReadNotification,
  onReadAllNotifications,
  onClearNotification,
}: NotificationDropdownProps) {
  const theme = useTheme();
  const [isClearAllModalOpen, setIsClearAllModalOpen] = useState(false);
  const [showUnread, setShowUnread] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [anchorElMarkAll, setAnchorElMarkAll] =
    React.useState<null | HTMLElement>(null);

  const handleRelatedReportClick = useHandleRelatedReportClick();
  const { redirectToReportUrl, redirectToListsUrl } = useReportRedirect();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isInSuggestedReportsControl = !useFeatureIsOn('oar-1064');
  const { redirect } = useRedirect();

  function filterfunction(notification: NormalizedNotification) {
    let isOkToShow = true;
    if (showUnread) {
      isOkToShow = !notification.read;
    }

    if (isOkToShow) {
      const isNotSuggestedReport = notification.type !== 'suggested_report';
      isOkToShow = isInSuggestedReportsControl || isNotSuggestedReport;
    }
    return isOkToShow;
  }

  const filteredNotifications = notifications.filter((noti) =>
    filterfunction(noti)
  );

  const {
    currentData,
    maxPage: maxPageCount,
    currentPage: currentPageCount,
    next: nextPage,
    prev: prevPage,
    jump: jumpToPage,
  } = usePagination([...filteredNotifications], PER_PAGE);

  const paginatedNotifications = currentData() as NormalizedNotification[];

  const resetToPageOne = () => {
    if (maxPageCount >= 1 && currentPageCount !== 1) {
      jumpToPage(1);
    }
  };

  useEffect(() => {
    resetToPageOne();
  }, [showUnread, open]);

  const onNotificationClick = (notification: NormalizedNotification) => {
    if (notification.type === 'suggested_report') {
      handleRelatedReportClick(notification);
    } else if (notification.type === 'report_update') {
      redirectToReportUrl(
        notification.reportType as ValidReportTypes,
        notification.permalink
      );
    } else if (notification.type === 'lizano_bulk_search') {
      redirectToListsUrl(notification.listCode || '');
    } else if (notification.type === 'new_feature' && notification.data?.link) {
      // redirect to the notification.link subroute
      redirect(notification.data.link);
    }
  };

  const handleMenuClickMarkAll = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorElMarkAll(event.currentTarget);
    setMenuIsOpen(true);
  };

  const handleCloseMenuMarkAll = () => {
    setMenuIsOpen(false);
    setAnchorElMarkAll(null);
  };

  const handleMarkAllAsRead = () => {
    onReadAllNotifications();
    notifications.forEach((notification) => {
      notification.read = true;
    });
    handleCloseMenuMarkAll();
  };

  const handleClearAllNotifications = () => {
    if (!onClearNotification) return;

    notifications.forEach((notification) => {
      onClearNotification(notification.id);
    });
  };

  const handleOpenClearAllModal = () => {
    setIsClearAllModalOpen(true);
    handleCloseMenuMarkAll();
  };

  const toggleShowUnread = () => {
    setShowUnread(!showUnread);
  };

  return (
    <Menu
      id="notification-dropdown"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        style: {
          width: '100%',
          maxWidth: isMobile ? undefined : theme.spacing(50),
        },
      }}
      sx={{
        '.MuiList-root': {
          paddingBottom: '0px',
        },
        '#notification-pagination': {
          py: '12px',
        },
      }}
      data-cy="notification-dropdown"
    >
      <Grid
        container
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        padding={theme.spacing(2)}
        paddingBottom={0}
      >
        <Text variant="h2">Notifications</Text>
        <IconButton
          onClick={handleMenuClickMarkAll}
          data-testid="notification-dropdown-options"
        >
          <Icons.MoreVert color="primary" sx={{ transform: 'rotate(90deg)' }} />
        </IconButton>
      </Grid>
      <Menu
        id="notification-dropdown-options"
        anchorEl={anchorElMarkAll}
        open={menuIsOpen}
        onClose={handleCloseMenuMarkAll}
      >
        <StyledMenuItem onClick={handleMarkAllAsRead}>
          <Icons.DoneAll fontSize="small" />
          <Text>Mark all as read</Text>
        </StyledMenuItem>
        {!!notifications.length && (
          <StyledMenuItem onClick={handleOpenClearAllModal}>
            <Icons.CleaningServices fontSize="small" />
            <Text>Clear all notifications</Text>
          </StyledMenuItem>
        )}
      </Menu>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 16px',
          paddingTop: '0px',
        }}
      >
        <Switch
          checked={showUnread}
          onChange={toggleShowUnread}
          data-testid="notification-dropdown-filter"
        />
        <Text>{showUnread ? 'Unread' : 'All'}</Text>
      </Box>

      {!filteredNotifications.length ? (
        <Text variant="h4" style={{ padding: '16px' }}>
          {showUnread ? 'No unread notifications' : 'No new notifications'}
        </Text>
      ) : (
        paginatedNotifications.map((paginatedNoti) => {
          return (
            <MenuItem
              key={paginatedNoti.id}
              onClick={handleClose}
              sx={{
                paddingBottom: 0,
                borderBottom: `1px solid ${theme.palette.secondary.light}`,
              }}
            >
              <NotificationCard
                notification={paginatedNoti}
                onReadNotification={onReadNotification}
                onNotificationClick={onNotificationClick}
              />
            </MenuItem>
          );
        })
      )}
      {!!filteredNotifications.length && (
        <NotificationDropdownPagination
          currentPage={currentPageCount}
          maxPage={maxPageCount}
          onClickPrev={prevPage}
          onClickNext={nextPage}
        />
      )}
      <ClearAllNotificationsModal
        isOpen={isClearAllModalOpen}
        setIsOpen={setIsClearAllModalOpen}
        clearAllNotifications={handleClearAllNotifications}
      />
    </Menu>
  );
}
